import { useQuery, useMutation, useQueryClient, useIsMutating } from '@tanstack/react-query';
import { businessAccountsApi, metaApi, ApiError } from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors';
import { useAuth } from '@/components/AuthProvider';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { getLimitErrorMessage, isLimitExceededError } from '@/hooks/useFeatureLimits';
import { isCoexistenceDegraded } from '@/lib/whatsapp/coexistenceState';
import {
  hasAccountRestrictions,
  type MetaAccountRestriction,
} from '@/lib/whatsapp/accountRestrictions';
import { isReviewBlocked } from '@/lib/whatsapp/accountReview';
import {
  describeHistorySyncRefusal,
  resolveHistorySyncPollTier,
} from '@/lib/whatsapp/historySync';

export interface BusinessAccount {
  id: string;
  name: string;
  phone_number: string;
  phone_number_id: string;
  business_account_id: string;
  access_token_encrypted: string;
  is_active: boolean;
  is_verified?: boolean;
  quality_rating?: string;
  connection_type?: string | null;
  coexistence_state?: 'OFFBOARDED' | 'DISCONNECTED' | null;
  coexistence_reason?: string | null;
  coexistence_state_at?: string | null;
  coexistence_onboarded_at?: string | null;
  history_sync_status?: 'REQUESTED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'REFUSED' | null;
  history_sync_requested_at?: string | null;
  history_sync_progress?: number | null;
  history_sync_progress_at?: string | null;
  history_sync_completed_at?: string | null;
  history_sync_error?: string | null;
  review_decision?: string | null;
  review_decision_at?: string | null;
  lock_status?: string | null;
  lock_detected_at?: string | null;
  restrictions?: MetaAccountRestriction[] | null;
  lead_form_layout?: unknown | null;
  created_at: string;
  updated_at: string;
}

// A locked, restricted or degraded account changes state on Meta's side over minutes
// to days; polling it every minute cost a request (and its DB reads) per open tab.
export const DEGRADED_ACCOUNT_POLL_MS = 5 * 60 * 1000;

export const HISTORY_SYNC_POLL_MS = 10 * 1000;

export function businessAccountsRefetchInterval(
  accounts: BusinessAccount[] | undefined
): number | false {
  if (!Array.isArray(accounts)) return false;

  const tiers = accounts.map((account) => resolveHistorySyncPollTier(account));

  if (tiers.some((tier) => tier === 'fast')) return HISTORY_SYNC_POLL_MS;

  const anyStalled = tiers.some((tier) => tier === 'slow');

  const anyDegraded = accounts.some(
    (account) =>
      account?.lock_status === 'LOCKED' ||
      isCoexistenceDegraded(account?.coexistence_state) ||
      hasAccountRestrictions(account?.restrictions) ||
      isReviewBlocked(account?.review_decision)
  );

  return anyStalled || anyDegraded ? DEGRADED_ACCOUNT_POLL_MS : false;
}

export function useBusinessAccounts() {
  const { user } = useAuth();

  const query = useQuery<BusinessAccount[]>({
    queryKey: ['business-accounts-v2', user?.id || 'no-user'],
    queryFn: async (): Promise<BusinessAccount[]> => {
      const result = await businessAccountsApi.getAll({
        is_active: true
      });

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return Array.isArray(result.data) ? result.data : [];
    },
    enabled: !!user, // Only fetch when user is available
    staleTime: 2 * 60 * 1000, // Data stays fresh for 2 minutes (reduced from 5 to get quality updates faster)
    gcTime: 10 * 60 * 1000, // Cache for 10 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on mount if data exists
    refetchInterval: (query) =>
      businessAccountsRefetchInterval(query.state.data),
    // Polls only while the tab is visible. The 10 s history-sync tier already ends on
    // its own: it drops to the slow tier once progress stalls and stops at the watch limit.
    refetchIntervalInBackground: false,
  });

  return query;
}

export function useWorkspaceBusinessAccounts(workspaceId: string | null | undefined) {
  const { user } = useAuth();

  return useQuery<BusinessAccount[]>({
    queryKey: ['business-accounts-workspace', workspaceId],
    queryFn: async (): Promise<BusinessAccount[]> => {
      const result = await businessAccountsApi.getAll({
        is_active: true,
        workspace_id: workspaceId!,
      });

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return Array.isArray(result.data) ? result.data : [];
    },
    enabled: !!user && !!workspaceId,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchInterval: (query) => businessAccountsRefetchInterval(query.state.data),
  });
}

export function useCreateBusinessAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (accountData: {
      name: string;
      phone_number: string;
      phone_number_id: string;
      business_account_id: string;
      access_token: string;
    }) => {
      const result = await businessAccountsApi.create({
        name: accountData.name,
        phone_number: accountData.phone_number,
        phone_number_id: accountData.phone_number_id,
        business_account_id: accountData.business_account_id,
        access_token_encrypted: accountData.access_token, // In production, encrypt this
      });

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-accounts-v2'] });
      toast({ variant: "success",
        title: "Business account added",
        description: "WhatsApp Business account has been successfully configured.",
      });
    },
    onError: (error: any) => {
      const errorMessage = getLimitErrorMessage(error);
      toast({
        title: isLimitExceededError(error) ? "Limit Reached" : "Error",
        description: errorMessage,
        variant: "destructive",
        duration: isLimitExceededError(error) ? 8000 : 5000,
      });
    }
  });
}

export const SYNC_CHAT_HISTORY_MUTATION_KEY = ['sync-chat-history'] as const;

export function useIsSyncingChatHistory(): boolean {
  return useIsMutating({ mutationKey: SYNC_CHAT_HISTORY_MUTATION_KEY }) > 0;
}

export function useSyncChatHistory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: SYNC_CHAT_HISTORY_MUTATION_KEY,
    mutationFn: async (accountId: string) => {
      const result = await metaApi.syncChatHistory(accountId);
      return { accountId, result };
    },
    onSuccess: async ({ result }) => {
      await queryClient.invalidateQueries({ queryKey: ['business-accounts-v2'] });

      if (result.success) {
        toast({
          title: 'Importing chat history',
          description:
            'Meta has accepted the request. Conversations and contacts will appear over the next few minutes.',
          variant: 'success',
        });
        return;
      }

      const detail = `${result.error || ''} ${result.details || ''}`;
      const isRateLimit =
        result.code === 'rate_limited' ||
        /request limit reached|request limit|rate limit|\(#4\)|\B#4\b/i.test(detail);

      toast({
        title:
          result.code === 'window_expired'
            ? 'Import window closed'
            : result.code === 'already_completed'
              ? 'Already imported'
              : result.code === 'in_flight'
                ? 'Import already running'
                : isRateLimit
                  ? 'Please wait before retrying'
                  : 'Could not import chat history',
        description: result.code
          ? describeHistorySyncRefusal(result.code)
          : isRateLimit
            ? "Meta's API rate limit has been reached. Wait 15-30 minutes and try again — the 24-hour import window is still running, so don't leave it too long."
            : result.details || result.error || 'Meta refused the request.',
        variant:
          result.code === 'already_completed' || result.code === 'in_flight'
            ? 'default'
            : isRateLimit
              ? 'warning'
              : 'destructive',
      });
    },
    onError: (error) => {
      showApiError(error, { fallback: { title: 'Failed to start chat history import' } });
    },
  });
}

export function useUnlinkBusinessAccount() {
  const queryClient = useQueryClient();
  const { setSelectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (accountId: string) => {
      try {
        const result = await metaApi.unlinkBusinessAccount(accountId);

        if (result.error) {
          throw ApiError.fromResult(result);
        }

        return result.data;
      } catch (error) {
        throw error;
      }
    },
    onSuccess: (data, accountId) => {
      const currentSelected = localStorage.getItem('selectedBusinessAccountId');
      if (currentSelected === accountId) {
        setSelectedAccountId(null);
        localStorage.removeItem('selectedBusinessAccountId');
      }

      queryClient.invalidateQueries({ queryKey: ['business-accounts-v2'] });
      toast({
        title: "Business account delinked",
        description: "Business account has been successfully delinked from Meta OAuth. You can reconnect it anytime.",
        variant: "success",
      });
    },
    onError: (error) => {
      showApiError(error, { fallback: { title: "Failed to delink business account" } });
    }
  });
}

